import { type QueryObserverOptions, useQuery } from '@tanstack/react-query';

import {
  type GetXvsVaultPoolCountOutput,
  getXvsVaultPoolCount,
} from 'clients/api/queries/getXvsVaultPoolCount';
import FunctionKey from 'constants/functionKey';
import { useGetContractAddress } from 'hooks/useGetContractAddress';
import { useGetToken } from 'libs/tokens';
import { useChainId, usePublicClient } from 'libs/wallet';
import type { ChainId } from 'types';

export type UseGetXvsVaultPoolCountQueryKey = [
  FunctionKey.GET_XVS_VAULT_POOLS_COUNT,
  { chainId: ChainId },
];

type Options = QueryObserverOptions<
  GetXvsVaultPoolCountOutput,
  Error,
  GetXvsVaultPoolCountOutput,
  GetXvsVaultPoolCountOutput,
  UseGetXvsVaultPoolCountQueryKey
>;

export const useGetXvsVaultPoolCount = (options?: Partial<Options>) => {
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { address: xvsVaultContractAddress } = useGetContractAddress({
    name: 'XvsVault',
  });

  const xvs = useGetToken({
    symbol: 'XVS',
  });

  return useQuery({
    queryKey: [FunctionKey.GET_XVS_VAULT_POOLS_COUNT, { chainId }],
    queryFn: () => {
      if (!xvsVaultContractAddress || !xvs?.address) {
        return { poolCount: 0 };
      }

      return getXvsVaultPoolCount({
        xvsVaultContractAddress,
        xvsTokenAddress: xvs.address,
        publicClient,
      });
    },
    ...options,
  });
};
